import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Form, Button, Alert, Tabs, Tab, Card } from 'react-bootstrap';
import { projectsApi, galleryApi } from '../services/api';
import { generateUniqueSlug } from '../services/slugUtils';
import '../styles/pages.css';

const ProjectForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [activeTab, setActiveTab] = useState<string>('info');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Form data
  const [formData, setFormData] = useState<any>({
    titulo: '',
    slug: '',
    descricao: '',
    dataProjeto: new Date().toISOString().split('T')[0],
    local: '',
    status: 'Planejado',
    categoriaLabel: 'Projeto',
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Gallery data
  const [projectImages, setProjectImages] = useState<{ id?: number; url: string; isPrincipal?: boolean }[]>([]);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [galleryId, setGalleryId] = useState<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (isEditing) {
      fetchProject();
    }
  }, [id, navigate, isEditing]);

  const fetchProject = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const data = await projectsApi.getById(parseInt(id));

      if (!data) {
        setError('Projeto não encontrado');
        setTimeout(() => navigate('/admin/projetos'), 2000);
        return;
      }

      console.log('📦 Projeto carregado:', data);

      setFormData({
        titulo: data.titulo || '',
        slug: data.slug || '',
        descricao: data.descricao || '',
        dataProjeto: data.dataProjeto ? data.dataProjeto.split('T')[0] : new Date().toISOString().split('T')[0],
        local: data.local || '',
        status: data.status || 'Planejado',
        ativo: data.ativo !== undefined ? data.ativo : true,
        categoriaLabel: data.categoriaLabel || 'Projetos',
      });
      setSlugManuallyEdited(true); // em edição, preserva o slug existente

      // Carrega a galeria e as imagens do projeto (se houver)
      if (data.idGaleriaFotos) {
        setGalleryId(data.idGaleriaFotos);
      }

      if (data.galeriaFotos?.fotos?.length > 0) {
        const sortedPhotos = [...data.galeriaFotos.fotos].sort((a, b) => a.ordem - b.ordem);
        setProjectImages(sortedPhotos.map(foto => ({ id: foto.id, url: foto.url, isPrincipal: foto.isPrincipal })));
      }
    } catch (err: any) {
      console.error('❌ Error loading project:', err);
      setError('Erro ao carregar projeto: ' + (err.message || 'Erro desconhecido'));

      if (err.message?.includes('401') || err.message?.includes('expirada')) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddImage = () => {
    const url = newImageUrl.trim();
    if (!url) return;
    setProjectImages(prev => [...prev, { url }]);
    setNewImageUrl('');
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingImage(true);
    setError('');
    try {
      const results = await Promise.all(files.map(f => galleryApi.uploadPhoto(f)));
      setProjectImages(prev => {
        const novasFotos = results.map((r, i) => ({
          url: r.url,
          isPrincipal: prev.length === 0 && i === 0,
        }));
        return [...prev, ...novasFotos];
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao fazer upload');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = async (index: number) => {
    const photo = projectImages[index];
    if (!window.confirm('Remover esta foto?')) return;

    // Se já existe no banco, deleta
    if (photo.id) {
      try {
        await galleryApi.deletePhoto(photo.url);
      } catch (err) {
        console.error('Erro ao deletar foto:', err);
      }
    }

    setProjectImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSetPrincipal = (index: number) => {
    setProjectImages(prev =>
      prev.map((p, i) => ({ ...p, isPrincipal: i === index }))
    );
  };

  const handleMoveImage = (index: number, direction: 'up' | 'down') => {
    const newImages = [...projectImages];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newImages.length) return;
    [newImages[index], newImages[swapIndex]] = [newImages[swapIndex], newImages[index]];
    setProjectImages(newImages);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validações
    if (!formData.titulo?.trim()) {
      setError('O título é obrigatório');
      setActiveTab('info');
      return;
    }

    if (!formData.descricao?.trim()) {
      setError('A descrição é obrigatória');
      setActiveTab('info');
      return;
    }

    if (!formData.dataProjeto) {
      setError('A data é obrigatória');
      setActiveTab('info');
      return;
    }

    setLoading(true);

    try {
      console.log('=== SALVANDO PROJETO ===');
      console.log('Token presente:', !!localStorage.getItem('admin_token'));
      console.log('📦 Dados do projeto:', formData);
      console.log('📸 Imagens:', projectImages.length);

      // Gera slug único se ainda não tem (criação nova)
      let slug = formData.slug;
      if (!slug) {
        slug = await generateUniqueSlug(formData.titulo, projectsApi.slugExists);
      }

      let projectId: number;
      let currentGalleryId = galleryId;

      if (isEditing) {
        await projectsApi.update(parseInt(id!), { ...formData, slug });
        projectId = parseInt(id!);
      } else {
        const result = await projectsApi.create({ ...formData, slug });
        projectId = result.id;
        currentGalleryId = result.idGaleriaFotos ?? null;
        setGalleryId(currentGalleryId);
      }

      // Salva fotos novas (sem id) na galeria
      if (projectImages.length > 0) {
        // Garante que há uma galeria vinculada
        if (!currentGalleryId) {
          const newGallery = await galleryApi.create({ titulo: formData.titulo });
          currentGalleryId = newGallery.id;
          setGalleryId(currentGalleryId);
          await projectsApi.update(projectId, { ...formData, idGaleriaFotos: currentGalleryId } as any);
        }

        // Salva todas as fotos com a ordem e isPrincipal corretos
        for (let i = 0; i < projectImages.length; i++) {
          const photo = projectImages[i];
          const ordem = i + 1;
          const isPrincipal = photo.isPrincipal ?? false;

          if (photo.id) {
            // Atualiza ordem e capa das fotos existentes
            await galleryApi.updatePhoto(photo.id, { ordem, isPrincipal });
          } else {
            // Insere fotos novas
            const saved = await galleryApi.addPhoto(currentGalleryId!, {
              url: photo.url,
              legenda: '',
              ordem,
              isPrincipal,
            });
            setProjectImages(prev =>
              prev.map((p, idx) => idx === i ? { ...p, id: saved.id } : p)
            );
          }
        }
      }

      setSuccess(isEditing
        ? 'Projeto atualizado com sucesso! Você pode voltar para a listagem.'
        : 'Projeto criado com sucesso!');

      // Redireciona apenas após criar (não após editar)
      if (!isEditing) {
        setTimeout(() => {
          navigate('/admin/projetos');
        }, 1500);
      }
    } catch (err: any) {
      console.error('❌ ERRO ao salvar projeto:', err);

      let errorMessage = 'Erro desconhecido ao salvar projeto';
      if (err.message) {
        errorMessage = err.message;
      }

      if (errorMessage.includes('expirada') || errorMessage.includes('401') || errorMessage.includes('Sessão')) {
        errorMessage = 'Sua sessão expirou. Faça login novamente.';
        setError(errorMessage);
        localStorage.removeItem('admin_token');
        setTimeout(() => navigate('/admin/login'), 2000);
        return;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="bi bi-folder me-2"></i>
          {isEditing ? 'Editar Projeto' : 'Novo Projeto'}
        </h2>
      </div>

      <div>
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            <div className="d-flex justify-content-between align-items-center">
              <span>{success}</span>
              {isEditing && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => navigate('/admin/projetos')}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Voltar para listagem
                </Button>
              )}
            </div>
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card>
          <Card.Body>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || 'info')}
              className="mb-4"
            >
              <Tab eventKey="info" title={<><i className="bi bi-info-circle me-2"></i>Informações</>}>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Título *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.titulo}
                      onChange={(e) => {
                        const titulo = e.target.value;
                        setFormData((prev: any) => ({
                          ...prev,
                          titulo,
                          slug: (!isEditing && !slugManuallyEdited)
                            ? titulo.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[°º]/g, 'o').replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 100)
                            : prev.slug,
                        }));
                      }}
                      required
                      placeholder="Digite o título do projeto"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Slug (URL) {isEditing && <small className="text-muted ms-1">— não altere após publicar</small>}
                    </Form.Label>
                    <div className="input-group">
                      <span className="input-group-text text-muted" style={{ fontSize: '0.85rem' }}>/projetos/</span>
                      <Form.Control
                        type="text"
                        value={formData.slug}
                        onChange={(e) => {
                          setSlugManuallyEdited(true);
                          setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') });
                        }}
                        placeholder="gerado-automaticamente-do-titulo"
                      />
                    </div>
                    <Form.Text className="text-muted">Gerado automaticamente do título. Só altere se necessário.</Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Descrição *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={8}
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      required
                      placeholder="Digite a descrição completa do projeto"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Data do Projeto *</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.dataProjeto}
                          onChange={(e) => setFormData({ ...formData, dataProjeto: e.target.value })}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Local</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.local}
                          onChange={(e) => setFormData({ ...formData, local: e.target.value })}
                          placeholder="Ex: São Paulo - SP"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status *</Form.Label>
                        <Form.Select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          required
                        >
                          <option value="Planejado">Planejado</option>
                          <option value="Em Andamento">Em Andamento</option>
                          <option value="Concluído">Concluído</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Categoria / Tipo</Form.Label>
                        <Form.Select
                          value={formData.categoriaLabel}
                          onChange={(e) => setFormData({ ...formData, categoriaLabel: e.target.value })}
                        >
                          <option value="Projeto">Projeto</option>
                          <option value="Ação social">Ação social</option>
                          <option value="Capacitação">Capacitação</option>
                          <option value="Dia de campo">Dia de campo</option>
                          <option value="Programa">Programa</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end gap-2">
                    <Button variant="secondary" onClick={() => navigate('/admin/projetos')}>
                      Cancelar
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Salvando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg me-2"></i>
                          {isEditing ? 'Atualizar' : 'Criar'}
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Tab>

              <Tab eventKey="fotos" title={<><i className="bi bi-images me-2"></i>Fotos ({projectImages.length})</>}>
                <div className="mb-4">
                  <h5>Galeria de Fotos</h5>
                  <p className="text-muted">
                    Faça upload das fotos ou insira a URL. A primeira foto será a capa do projeto.
                  </p>

                  <Row className="mb-4 g-3">
                    <Col md={6}>
                      <Form.Label>Upload de arquivos</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && (
                        <div className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Enviando...
                        </div>
                      )}
                    </Col>
                    <Col md={6}>
                      <Form.Label>Ou insira a URL</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="https://... ou /img/projects/..."
                          value={newImageUrl}
                          onChange={(e) => setNewImageUrl(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddImage(); } }}
                        />
                        <Button variant="outline-primary" onClick={handleAddImage}>
                          <i className="bi bi-plus-lg"></i>
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {projectImages.length === 0 ? (
                    <div className="text-center py-5" style={{ background: 'var(--color-gray-light)', borderRadius: '8px' }}>
                      <i className="bi bi-images" style={{ fontSize: '3rem', color: 'var(--color-gray)' }}></i>
                      <p className="text-muted mt-3">Nenhuma foto adicionada ainda</p>
                    </div>
                  ) : (
                    <Row className="g-3">
                      {projectImages.map((photo, index) => (
                        <Col key={index} md={4} sm={6}>
                          <Card>
                            <div
                              style={{
                                height: '180px',
                                backgroundImage: `url('${photo.url}')`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundColor: '#f0f0f0',
                              }}
                            />
                            <Card.Body className="p-2">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-1">
                                  <Button
                                    variant={photo.isPrincipal ? 'warning' : 'outline-secondary'}
                                    size="sm"
                                    title="Definir como capa"
                                    onClick={() => handleSetPrincipal(index)}
                                  >
                                    <i className={`bi bi-star${photo.isPrincipal ? '-fill' : ''}`}></i>
                                  </Button>
                                  <div className="d-flex gap-1 ms-1">
                                    <Button variant="outline-secondary" size="sm" onClick={() => handleMoveImage(index, 'up')} disabled={index === 0}>
                                      <i className="bi bi-arrow-up"></i>
                                    </Button>
                                    <Button variant="outline-secondary" size="sm" onClick={() => handleMoveImage(index, 'down')} disabled={index === projectImages.length - 1}>
                                      <i className="bi bi-arrow-down"></i>
                                    </Button>
                                  </div>
                                </div>
                                <Button variant="outline-danger" size="sm" onClick={() => handleRemoveImage(index)}>
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button variant="secondary" onClick={() => navigate('/admin/projetos')}>
                      Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Salvando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-lg me-2"></i>
                          {isEditing ? 'Atualizar' : 'Criar'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ProjectForm;
